import { createFileRoute, Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { ChevronRightIcon } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/app/page";
import { Card } from "@/components/ui/card";
import {
  GarageSettingsHint,
  SettingsGarage,
  type SettingsPath,
  useSettingsPages,
} from "@/features/settings/pages";

export const Route = createFileRoute("/settings/")({
  component: SettingsOverview,
});

/** Where the sections of the one-page settings went, so old links like `#members` still lead there. */
const OLD_SECTIONS: Record<string, SettingsPath> = {
  general: "/settings/appearance",
  users: "/settings/users",
  members: "/settings/members",
  locations: "/settings/locations",
  suppliers: "/settings/suppliers",
  tags: "/settings/tags",
  chassisCodes: "/settings/chassis-codes",
  installation: "/settings/installation",
  about: "/settings/about",
  garage: "/settings/garage",
  data: "/settings/data",
};

/** Settings › every page this person sees, by group. */
function SettingsOverview() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { groups } = useSettingsPages();
  const hash = useRouterState({ select: (state) => state.location.hash });
  useEffect(() => {
    const old = OLD_SECTIONS[hash];
    if (old) void navigate({ to: old, replace: true });
  }, [hash, navigate]);
  return (
    <>
      <PageHeader title={t("settings.title")} />
      <div className="flex max-w-3xl flex-col gap-8">
        <div className="sm:w-72">
          <SettingsGarage />
        </div>
        {groups.map(({ group, pages }) => (
          <section
            key={group}
            aria-labelledby={`settings-${group}`}
            className="flex flex-col gap-2"
          >
            <div>
              <h2 id={`settings-${group}`} className="text-lg font-semibold tracking-tight">
                {t(`settings.groups.${group}`)}
              </h2>
              {group === "garage" ? (
                <p className="mt-0.5 text-sm text-muted-foreground">
                  <GarageSettingsHint />
                </p>
              ) : null}
            </div>
            <Card className="divide-y divide-border overflow-hidden">
              {pages.map((page) => (
                <Link
                  key={page.to}
                  to={page.to}
                  className="flex items-center gap-3 px-4 py-3 transition-colors outline-none hover:bg-muted focus-visible:bg-muted"
                >
                  <page.icon className="size-5 shrink-0 text-muted-foreground" />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="text-sm font-medium">{t(page.label)}</span>
                    <span className="text-xs text-muted-foreground">{t(page.body)}</span>
                  </span>
                  <ChevronRightIcon className="size-4 shrink-0 text-muted-foreground" />
                </Link>
              ))}
            </Card>
          </section>
        ))}
      </div>
    </>
  );
}
